import { useState } from 'react';

const ContractorDirectory = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const contractors = [
    {
      name: 'El Paso Premier Fence Company',
      image: 'https://public.readdy.ai/ai/img_res/d23dfc7506baa92d2c578a4671fbc08c.jpg',
      specialty: 'Wood & Vinyl Fencing Specialists',
      experience: '18+ Years Experience',
      services: ['Custom Wood Fencing', 'Vinyl Fence Installation', 'Fence Repair & Staining', 'Gate Automation'],
      rating: 4.9,
      reviews: 287,
      description: 'Family-owned fence company specializing in custom wood and vinyl fencing for El Paso residential and commercial properties.'
    },
    {
      name: 'Southwest Security Fence Solutions',
      image: 'https://readdy.ai/api/search-image?query=Expert%20fence%20contractors%20installing%20ornamental%20iron%20security%20fence%2C%20professional%20team%20with%20welding%20equipment%2C%20commercial%20property%20setting%2C%20simple%20background%20highlighting%20security%20fencing%20expertise%20and%20quality%20installation&width=600&height=400&seq=fence-contractor-002&orientation=landscape',
      specialty: 'Commercial & Security Fencing',
      experience: '22+ Years Experience',
      services: ['Chain Link Fencing', 'Wrought Iron Installation', 'Security Fence Systems', 'Commercial Fencing'],
      rating: 4.8,
      reviews: 341,
      description: 'Leading commercial fence contractor providing high-security fencing solutions and ornamental iron work throughout El Paso.'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5u6m3ckjhtioh5jrjl0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Top-Rated Fence Contractors in El Paso
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Connect with experienced, licensed fence contractors who deliver quality installations and exceptional customer service throughout El Paso and surrounding areas.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {contractors.map((contractor, index) => (
            <div
              key={index}
              className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="w-full h-64">
                <img
                  src={contractor.image}
                  alt={contractor.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {contractor.name}
                    </h3>
                    <p className="text-orange-600 font-semibold mb-1">
                      {contractor.specialty}
                    </p>
                    <p className="text-sm text-gray-600">
                      {contractor.experience}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 mb-1">
                      <i className="ri-star-fill text-yellow-400"></i>
                      <span className="font-bold text-gray-900">{contractor.rating}</span>
                    </div>
                    <p className="text-xs text-gray-600">{contractor.reviews} reviews</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  {contractor.description}
                </p>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Services Offered:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {contractor.services.map((service, idx) => (
                      <div key={idx} className="flex items-start">
                        <i className="ri-check-line text-orange-600 mr-2 mt-1 flex-shrink-0"></i>
                        <span className="text-sm text-gray-700">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-8 md:p-12">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Have a Pro Contact You
            </h3>
            <p className="text-gray-700">
              Fill out the form below and a qualified fence contractor will reach out to discuss your project and provide a free estimate.
            </p>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800">
              Thank you! A fence professional will contact you shortly.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              Something went wrong. Please try again.
            </div>
          )}

          <form onSubmit={handleSubmit} data-readdy-form id="fence-contact-form">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="contact-name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm bg-white"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm bg-white"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="contact-phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm bg-white"
                />
              </div>
              <div>
                <label htmlFor="contact-service" className="block text-sm font-medium text-gray-700 mb-2">
                  Service Needed *
                </label>
                <select
                  id="contact-service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm bg-white"
                >
                  <option value="">Select a service</option>
                  <option value="New Fence Installation">New Fence Installation</option>
                  <option value="Fence Repair">Fence Repair</option>
                  <option value="Gate Installation">Gate Installation</option>
                  <option value="Fence Staining/Painting">Fence Staining/Painting</option>
                  <option value="Fence Removal">Fence Removal</option>
                  <option value="Consultation">Consultation</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">
                Project Details
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows={4}
                maxLength={500}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm resize-none bg-white"
                placeholder="Tell us about your fence project..."
              ></textarea>
              <p className="text-xs text-gray-600 mt-1">Maximum 500 characters</p>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-orange-600 text-white py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? 'Submitting...' : 'Request Contact from a Pro'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContractorDirectory;
